import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ImageCarousel from './ImageCarousel';
import { useLanguage } from '../contexts/LanguageContext';

export default function AnniversaryAnimation({ onComplete }) {
    const { t } = useLanguage();
    const [showAnimation, setShowAnimation] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowAnimation(false);
            setTimeout(() => {
                onComplete();
            }, 500);
        }, 3000); // 3 seconds for a more relaxed experience

        return () => clearTimeout(timer);
    }, [onComplete]);

    return (
        <AnimatePresence>
            {showAnimation && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden"
                >
                    {/* Hero Section Background with Blur */}
                    <div className="absolute inset-0">
                        <div style={{ filter: 'blur(50px)', transform: 'scale(1.1)' }} className="absolute inset-0">
                            <ImageCarousel />
                        </div>
                        {/* Dark overlay with gradient */}
                        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
                    </div>

                    {/* Elegant decorative line - top */}
                    <motion.div
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={{ scaleX: 1, opacity: 0.6 }}
                        transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute top-20 left-1/2 transform -translate-x-1/2 h-px w-64 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent z-10"
                    />

                    {/* Main content container */}
                    <div className="relative z-10 text-center px-8">
                        {/* "DAL 1986" - appears first */}
                        <motion.p
                            initial={{ opacity: 0, y: -30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="text-sm md:text-base text-gray-400 tracking-[0.4em] font-light mb-8"
                            style={{
                                fontFamily: "'Raleway', sans-serif",
                                fontWeight: 300
                            }}
                        >
                            {t.anniversary.since}
                        </motion.p>

                        {/* Main "40" number with logo - elegant entrance */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.6, duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                            className="relative mb-6 flex items-center justify-center gap-4 md:gap-8"
                        >
                            {/* Ellegi Logo - to the left of 40 */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.8, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                                className="flex-shrink-0"
                            >
                                <img
                                    src="./assets/EllegiLogoStartup.png"
                                    alt="Ellegi Logo"
                                    className="w-24 md:w-32 lg:w-40 h-auto opacity-90"
                                    style={{
                                        filter: 'drop-shadow(0 0 20px rgba(212, 175, 55, 0.4))'
                                    }}
                                />
                            </motion.div>

                            {/* 40 with glow effect */}
                            <div className="relative">
                                {/* Glow effect behind */}
                                <motion.div
                                    className="absolute inset-0 flex items-center justify-center"
                                    animate={{
                                        opacity: [0.3, 0.5, 0.3],
                                        scale: [0.95, 1.05, 0.95]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                >
                                    <span className="text-[140px] md:text-[200px] lg:text-[260px] font-bold text-[#D4AF37] blur-3xl">
                                        40
                                    </span>
                                </motion.div>

                                {/* Main number */}
                                <motion.h1
                                    className="relative text-[140px] md:text-[200px] lg:text-[260px] font-bold text-transparent bg-clip-text bg-gradient-to-b from-[#F4E4C1] via-[#D4AF37] to-[#B8941F]"
                                    style={{
                                        fontFamily: "'Cinzel', serif",
                                        fontWeight: 900,
                                        letterSpacing: '0.05em',
                                        textShadow: '0 0 60px rgba(212, 175, 55, 0.6), 0 0 100px rgba(212, 175, 55, 0.3)'
                                    }}
                                    animate={{
                                        textShadow: [
                                            '0 0 60px rgba(212, 175, 55, 0.6), 0 0 100px rgba(212, 175, 55, 0.3)',
                                            '0 0 80px rgba(212, 175, 55, 0.8), 0 0 140px rgba(212, 175, 55, 0.4)',
                                            '0 0 60px rgba(212, 175, 55, 0.6), 0 0 100px rgba(212, 175, 55, 0.3)'
                                        ]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                >
                                    40
                                </motion.h1>
                            </div>
                        </motion.div>

                        {/* "ANNI DI ECCELLENZA" */}
                        <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.2, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="text-2xl md:text-3xl lg:text-4xl text-[#D4AF37] tracking-[0.25em] font-semibold mb-4"
                            style={{
                                fontFamily: "'Raleway', sans-serif",
                                fontWeight: 600,
                                textShadow: '0 0 30px rgba(212, 175, 55, 0.4)'
                            }}
                        >
                            {t.anniversary.years}
                        </motion.p>

                        {/* Company motto */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.8, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                            className="text-lg md:text-xl text-gray-300 font-light italic tracking-wide"
                            style={{
                                fontFamily: "'Lora', serif",
                                fontWeight: 400
                            }}
                        >
                            {t.anniversary.motto}
                        </motion.p>
                    </div>

                    {/* Elegant decorative line - bottom */}
                    <motion.div
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={{ scaleX: 1, opacity: 0.6 }}
                        transition={{ delay: 2, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute bottom-20 left-1/2 transform -translate-x-1/2 h-px w-64 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent z-10"
                    />

                    {/* Skip button */}
                    <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4 }}
                        whileHover={{
                            opacity: 1,
                            scale: 1.05
                        }}
                        transition={{ delay: 2.5 }}
                        onClick={() => {
                            setShowAnimation(false);
                            setTimeout(onComplete, 300);
                        }}
                        className="absolute bottom-8 right-8 text-gray-400 hover:text-[#D4AF37] transition-all text-sm tracking-widest font-light z-10"
                        style={{
                            fontFamily: "'Raleway', sans-serif"
                        }}
                    >
                        {t.anniversary.skip}
                    </motion.button>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
